<script setup lang="ts">
/** Dialogue d'attribution / réaffectation d'une demande à un technicien. */
import { ref, watch } from 'vue'
import AccompTechnicianPicker from '@/v2/accompagnement/components/AccompTechnicianPicker.vue'
import type { AccompRequest } from '@/v2/accompagnement/api/accompagnementTypes'

const props = defineProps<{ request: AccompRequest | null; loading?: boolean }>()
const emit = defineEmits<{
  close: []
  confirmed: [payload: { technicianUuid: string; note?: string }]
}>()

const dialog = ref<HTMLDialogElement | null>(null)
const technicianUuid = ref('')
const note = ref('')

watch(
  () => props.request,
  (req) => {
    if (req) {
      technicianUuid.value = ''
      note.value = ''
      if (!dialog.value?.open) dialog.value?.showModal()
    } else {
      dialog.value?.close()
    }
  },
)

function confirm() {
  if (!technicianUuid.value) return
  emit('confirmed', { technicianUuid: technicianUuid.value, note: note.value.trim() || undefined })
}

function onCancel() {
  emit('close')
}
</script>

<template>
  <dialog ref="dialog" class="modal" @close="onCancel">
    <div class="modal-box">
      <h3 class="text-lg font-semibold">
        {{ request?.assignedToUuid ? 'Réaffecter la demande' : 'Attribuer la demande' }}
      </h3>
      <p v-if="request" class="mt-1 text-sm text-base-content/70">
        {{ request.reference }} · {{ request.client.name }}
      </p>

      <div class="mt-4 space-y-4">
        <div>
          <label for="assign-tech" class="mb-1 block text-sm font-medium text-base-content">
            Technicien <span class="text-error" aria-hidden="true">*</span>
          </label>
          <AccompTechnicianPicker id="assign-tech" v-model="technicianUuid" />
        </div>
        <div>
          <label for="assign-note" class="mb-1 block text-sm font-medium text-base-content">
            Note (facultatif)
          </label>
          <textarea
            id="assign-note"
            v-model="note"
            rows="2"
            class="textarea textarea-bordered w-full text-base"
            placeholder="Consigne pour le technicien…"
          ></textarea>
        </div>
      </div>

      <div class="modal-action">
        <button type="button" class="btn btn-ghost" :disabled="loading" @click="onCancel">Annuler</button>
        <button
          type="button"
          class="btn btn-primary"
          :disabled="!technicianUuid || loading"
          :aria-busy="loading"
          @click="confirm"
        >
          <span v-if="loading" class="loading loading-spinner loading-xs" aria-hidden="true"></span>
          {{ request?.assignedToUuid ? 'Réaffecter' : 'Attribuer' }}
        </button>
      </div>
    </div>
    <form method="dialog" class="modal-backdrop">
      <button @click="onCancel">Fermer</button>
    </form>
  </dialog>
</template>
